<template>
  <div class="tw-bg-white tw-py-4 tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-h-[220px]">
    <div class="tw-flex tw-items-center tw-mb-6 tw-gap-3 tw-px-4">
      <div class="tw-flex tw-items-center tw-gap-2">
        <p class="tw-p-2x tw-font-bold tw-text-lg">{{ $t('common.spend-per-day') }}</p>
        <p class="tw-px-1 tw-bg-blue-600 tw-text-white tw-text-sm tw-rounded">${{ new Intl.NumberFormat().format(total) }}</p>
      </div>
    </div>
    <apexchart 
      type="area" 
      height="140" 
      :options="options" 
      :series="series"
    ></apexchart>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { marketer } from '@/api/dashboard';

const { t } = useI18n();
const loading = ref(true);
// Demo data - converting cost_per_lead to spend values
const data = ref([
  // { date: "2025-05-10", spend: 84.15 },
  // { date: "2025-05-12", spend: 192.82 },
  // { date: "2025-05-14", spend: 264.99 },
  // { date: "2025-05-16", spend: 76.05 },
  // { date: "2025-05-20", spend: 87.78 },
  // { date: "2025-05-21", spend: 80.00 },
  // { date: "2025-05-22", spend: 92.02 },
  // { date: "2025-05-23", spend: 79.20 },
  // { date: "2025-05-24", spend: 88.20 },
  // { date: "2025-05-26", spend: 75.27 },
  // { date: "2025-05-27", spend: 135.72 },
  // { date: "2025-05-28", spend: 84.87 },
  // { date: "2025-05-29", spend: 106.26 },
  // { date: "2025-05-30", spend: 79.60 },
  // { date: "2025-05-31", spend: 89.04 }
]);

// Calculations
const total = computed(() => data.value.reduce((acc, curr) => acc + curr.spend, 0));

// Chart configuration
const options = computed(() => ({
  chart: {
    type: 'area',
    toolbar: { show: false },
    zoom: { enabled: false },
    foreColor: '#64748b'
  },
  colors: ['#2563EB'],
  dataLabels: { 
    enabled: true,
    style: {
      fontSize: '10px',
      fontFamily: 'Inter, sans-serif',
      colors: ['#fff']
    },
    background: {
      enabled: true,
      foreColor: '#2563EB',
      padding: 4,
      borderRadius: 8,
      borderWidth: 0,
      opacity: 0.9,
    }
  },
  stroke: {
    width: 2,
    curve: 'smooth'
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 0.3,
      opacityFrom: 0.6,
      opacityTo: 0.1,
      stops: [0, 90, 100],
      colorStops: [
        [
          {
            offset: 0,
            color: '#2563EB',
            opacity: 0.6
          },
          {
            offset: 100,
            color: '#2563EB',
            opacity: 0.1
          }
        ]
      ]
    }
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        fontSize: '11px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
      },
      format: 'MMM d',
      datetimeUTC: false
    },
    axisTicks: { show: false }
  },
  yaxis: { show: false },
  grid: {
    borderColor: '#f1f5f9',
    strokeDashArray: 4,
    padding: { top: 0, right: 20, bottom: 0, left: 20 }
  },
  tooltip: {
    theme: 'light',
    style: {
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif'
    },
    x: {
      format: 'MMM d, yyyy'
    },
    y: {
      formatter: (value) => `$${value.toFixed(2)}`
    },
    marker: {
      fillColors: ['#2563EB']
    }
  }
}));

// Data format for datetime series
const series = computed(() => [{
  name: t('common.spend'),
  data: data.value.map(i => ({
    x: new Date(i.date).getTime(),
    y: i.spend
  }))
}]);

const getData = async () => {
    loading.value = true;
  await marketer.spendPerDay()
  .then(res => {
        data.value = res.data;
  })
  .catch(err => {
    console.log(err);
  })
  loading.value = false;
}

getData();

</script>

<style scoped>
.tw-bg-blue-600 {
  background-color: #2563EB;
}
</style> 